import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Home, ShoppingBag, X, Coins, CheckCircle2, Vault, DollarSign, Loader2 } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

/**
 * Marketplace — convert your Free Play (FP) into real Cash payouts.
 * Items + redemption are wired to /api/marketplace/* (admin-configurable).
 */
const FALLBACK_ITEMS = [
  { id: 'starter-1', name: 'Quick Cash',     fp: 100,    cash: 20,    cat: 'starter' },
  { id: 'starter-2', name: 'Mini Vault',     fp: 250,    cash: 50,    cat: 'starter' },
  { id: 'mid-1',     name: 'Lucky Stack',    fp: 500,    cash: 100,   cat: 'mid'     },
  { id: 'mid-2',     name: 'Gold Vault',     fp: 1000,   cash: 200,   cat: 'mid'     },
  { id: 'high-1',    name: 'Royal Vault',    fp: 2500,   cash: 500,   cat: 'high'    },
  { id: 'high-2',    name: 'Elite Cashout',  fp: 5000,   cash: 1000,  cat: 'high'    },
  { id: 'whale-1',   name: 'Whale Vault',    fp: 10000,  cash: 2000,  cat: 'whale'   },
];

const CAT_STYLE = {
  starter: { gradient: 'linear-gradient(135deg, #10B981, #059669)', accent: '#10B981', label: 'Starter' },
  mid:     { gradient: 'linear-gradient(135deg, #3B82F6, #1D4ED8)', accent: '#3B82F6', label: 'Mid'     },
  high:    { gradient: 'linear-gradient(135deg, #8B5CF6, #6D28D9)', accent: '#8B5CF6', label: 'High'    },
  whale:   { gradient: 'linear-gradient(135deg, #FBBF24, #DC2626)', accent: '#FBBF24', label: 'WHALE'   },
};

export default function MarketplacePage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const homePath = tenantSlug ? `/${tenantSlug}/app` : '/app';
  const explorePath = tenantSlug ? `/${tenantSlug}/app/explore` : '/app/explore';

  const [items, setItems] = useState(FALLBACK_ITEMS);
  const [fpBalance, setFpBalance] = useState(0);
  const [confirm, setConfirm] = useState(null); // selected item
  const [redeemed, setRedeemed] = useState(null); // success message item
  const [submitting, setSubmitting] = useState(false);

  const refreshBalance = async () => {
    try {
      const w = await api.get('/api/wallet/balance');
      setFpBalance(Number(w.data?.play_credits ?? w.data?.play_credits_balance ?? 0));
    } catch (_) {
      setFpBalance(0);
    }
  };

  useEffect(() => {
    let mounted = true;
    api.get('/api/marketplace/items')
      .then((r) => { if (mounted && Array.isArray(r.data?.items) && r.data.items.length) setItems(r.data.items); })
      .catch(() => { /* keep fallback */ });
    refreshBalance();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRedeem = (item) => setConfirm(item);
  const onConfirm = async () => {
    if (!confirm || submitting) return;
    const item = confirm;
    if (fpBalance < item.fp) {
      toast.error(`Need ${item.fp} FP — you have ${fpBalance}`);
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/marketplace/redeem', { item_id: item.id });
      setConfirm(null);
      setRedeemed(item);
      await refreshBalance();
      toast.success(`+$${item.cash} cash credited`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.response?.data?.message || 'Redemption failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="player-app min-h-screen pb-12"
      style={{ color: '#FFFFFF' }}
      data-testid="explore-sub-marketplace"
    >
      {/* Header */}
      <div
        className="sticky top-0 z-30 px-4 sm:px-6 py-4 flex items-center gap-3"
        style={{
          background: 'rgba(6,23,14,0.85)',
          backdropFilter: 'blur(18px) saturate(160%)',
          WebkitBackdropFilter: 'blur(18px) saturate(160%)',
          borderBottom: '1px solid rgba(245,158,11,0.45)',
        }}
      >
        <button
          onClick={() => navigate(explorePath)}
          className="w-10 h-10 rounded-full flex items-center justify-center transition active:scale-95"
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: '#FFFFFF',
          }}
          data-testid="marketplace-back"
          aria-label="Back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1
            className="display font-extrabold text-xl sm:text-2xl flex items-center gap-2"
            style={{ color: '#FFFFFF' }}
          >
            <span
              className="w-9 h-9 rounded-xl flex items-center justify-center marketplace-bag-anim"
              style={{
                background: 'linear-gradient(135deg, #FBBF24, var(--vl-page-marketplace-accent, #F59E0B))',
                boxShadow: '0 6px 16px rgba(245,158,11,0.55), inset 0 1px 0 rgba(255,255,255,0.45)',
              }}
              aria-hidden
            >
              <ShoppingBag className="w-5 h-5" style={{ color: '#3F2A00' }} strokeWidth={2.6} />
            </span>
            Marketplace
          </h1>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Exchange your FP credits to main balance
          </p>
        </div>
        {/* FP balance pill (cosmetic — tie to real wallet later) */}
        <div
          className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full"
          style={{
            background: 'rgba(245,158,11,0.10)',
            border: '1px solid rgba(245,158,11,0.40)',
            color: '#FCD34D',
          }}
        >
          <Coins className="w-4 h-4" />
          <span className="display font-extrabold text-sm">2,500 FP</span>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {items.map((it, idx) => {
            const cat = CAT_STYLE[it.cat] || CAT_STYLE.starter;
            const isWhale = it.cat === 'whale';
            return (
              <button
                key={it.id || idx}
                onClick={() => onRedeem(it)}
                data-testid={`marketplace-item-${idx}`}
                className="text-left rounded-[32px] p-4 sm:p-5 transition active:scale-[0.98] hover:brightness-110 flex flex-col gap-3 relative overflow-hidden"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: `1px solid ${cat.accent}66`,
                  boxShadow: `0 8px 22px ${cat.accent}1F, inset 0 1px 0 rgba(255,255,255,0.05)`,
                }}
                data-testid={`mp-item-${idx}`}
              >
                {isWhale && (
                  <span
                    className="absolute -top-1 -right-1 px-2 py-0.5 text-[9px] font-extrabold display tracking-wider rotate-12"
                    style={{
                      background: 'linear-gradient(135deg, #FCD34D, #DC2626)',
                      color: '#3F2A00',
                      boxShadow: '0 4px 10px rgba(245,158,11,0.55)',
                      borderRadius: '6px',
                      letterSpacing: '0.1em',
                    }}
                  >
                    WHALE
                  </span>
                )}

                {/* Top row: icon + tier label */}
                <div className="flex items-center gap-3">
                  <span
                    className="w-12 h-12 rounded-[20px] flex items-center justify-center shrink-0"
                    style={{
                      background: cat.gradient,
                      boxShadow: `0 8px 18px ${cat.accent}55, inset 0 1px 0 rgba(255,255,255,0.4)`,
                    }}
                    aria-hidden
                  >
                    <Vault className="w-6 h-6 text-white" strokeWidth={2.4} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: cat.accent }}>
                      {cat.label} · Tier {idx + 1}
                    </p>
                    <p
                      className="display font-extrabold leading-tight text-base sm:text-lg truncate"
                      style={{ color: '#FFFFFF' }}
                    >
                      {it.name}
                    </p>
                  </div>
                </div>

                {/* Conversion row: FP cost → Cash payout */}
                <div
                  className="flex items-stretch gap-2 rounded-[22px] p-2"
                  style={{
                    background: 'rgba(0,0,0,0.25)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <div className="flex-1 text-center px-2 py-1.5 rounded-[20px]" style={{ background: 'rgba(245,158,11,0.10)' }}>
                    <p className="text-[9px] uppercase tracking-wider font-bold" style={{ color: '#FCD34D' }}>Spend</p>
                    <p className="display font-extrabold text-base mono flex items-center justify-center gap-1" style={{ color: '#FCD34D' }}>
                      <Coins className="w-4 h-4" />
                      {it.fp.toLocaleString()}
                      <span className="text-[10px] font-bold ml-0.5">FP</span>
                    </p>
                  </div>
                  <div className="flex items-center" style={{ color: 'rgba(255,255,255,0.4)' }}>→</div>
                  <div className="flex-1 text-center px-2 py-1.5 rounded-[20px]" style={{ background: 'rgba(34,197,94,0.10)' }}>
                    <p className="text-[9px] uppercase tracking-wider font-bold" style={{ color: '#86EFAC' }}>Get Cash</p>
                    <p className="display font-extrabold text-base mono flex items-center justify-center" style={{ color: '#22FF88' }}>
                      <DollarSign className="w-4 h-4" />
                      {it.cash.toLocaleString()}
                    </p>
                  </div>
                </div>

                <span
                  className="display font-extrabold text-center text-sm py-2.5 rounded-full uppercase tracking-wider mt-1"
                  style={{
                    background: cat.gradient,
                    color: '#FFFFFF',
                    boxShadow: `0 6px 14px ${cat.accent}44`,
                  }}
                >
                  Redeem
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Confirm modal */}
      {confirm && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center px-4"
          style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(10px)' }}
          onClick={() => setConfirm(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="rounded-3xl w-full max-w-md p-6 sm:p-8 text-center relative"
            style={{
              background: '#FFFFFF',
              color: '#0B0F0C',
              boxShadow: '0 30px 70px rgba(0,0,0,0.5)',
            }}
            data-testid="mp-confirm-modal"
          >
            <button
              onClick={() => setConfirm(null)}
              className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.08)', color: '#0B0F0C' }}
              aria-label="Close"
              data-testid="mp-confirm-close"
            >
              <X className="w-4 h-4" />
            </button>
            <div
              className="w-20 h-20 mx-auto rounded-2xl flex items-center justify-center mb-4"
              style={{
                background: CAT_STYLE[confirm.cat].gradient,
                boxShadow: `0 12px 30px ${CAT_STYLE[confirm.cat].accent}77, inset 0 1px 0 rgba(255,255,255,0.5)`,
              }}
            >
              <Vault className="w-10 h-10 text-white" strokeWidth={2.5} />
            </div>
            <p className="display text-xs uppercase tracking-[0.2em] font-bold" style={{ color: CAT_STYLE[confirm.cat].accent }}>
              {CAT_STYLE[confirm.cat].label} TIER
            </p>
            <h2 className="display font-extrabold text-2xl sm:text-3xl mt-1 mb-3" style={{ color: '#0B0F0C' }}>
              {confirm.name}
            </h2>
            <div className="flex items-stretch gap-2 mt-2 mb-1">
              <div className="flex-1 text-center px-2 py-3 rounded-xl" style={{ background: '#FEF3C7' }}>
                <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#B45309' }}>You Spend</p>
                <p className="display font-extrabold text-lg flex items-center justify-center gap-1 mt-0.5" style={{ color: '#B45309' }}>
                  <Coins className="w-4 h-4" />
                  {confirm.fp.toLocaleString()}
                  <span className="text-xs font-bold ml-0.5">FP</span>
                </p>
              </div>
              <div className="flex items-center px-1" style={{ color: '#9CA3AF' }}>→</div>
              <div className="flex-1 text-center px-2 py-3 rounded-xl" style={{ background: '#DCFCE7' }}>
                <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#15803D' }}>You Get</p>
                <p className="display font-extrabold text-lg flex items-center justify-center mt-0.5" style={{ color: '#15803D' }}>
                  <DollarSign className="w-4 h-4" />
                  {confirm.cash.toLocaleString()}
                </p>
              </div>
            </div>
            <p className="text-xs mt-3" style={{ color: '#6B7280' }}>
              The cash will be paid out to your selected payout method.
            </p>
            <p className="text-xs mt-1" style={{ color: '#0B0F0C' }} data-testid="mp-fp-balance">
              Your FP balance: <span className="font-bold">{Number(fpBalance).toLocaleString()} FP</span>
            </p>
            <div className="grid grid-cols-2 gap-3 mt-6">
              <button
                onClick={() => setConfirm(null)}
                disabled={submitting}
                className="h-12 rounded-2xl display font-bold transition active:scale-[0.98] hover:brightness-110 disabled:opacity-60"
                style={{ background: '#F3F4F6', color: '#0B0F0C', border: '1px solid #E5E7EB' }}
                data-testid="mp-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={submitting || fpBalance < confirm.fp}
                className="h-12 rounded-2xl display font-extrabold text-white transition active:scale-[0.98] hover:brightness-110 disabled:opacity-60 flex items-center justify-center gap-2"
                style={{
                  background: 'linear-gradient(180deg, #22C55E, #15803D)',
                  boxShadow: '0 8px 22px rgba(34,197,94,0.45)',
                }}
                data-testid="mp-confirm-btn"
              >
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Redeeming…</> : (fpBalance < confirm.fp ? 'Insufficient FP' : 'Redeem')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success modal */}
      {redeemed && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center px-4"
          style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(10px)' }}
          onClick={() => setRedeemed(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="rounded-3xl w-full max-w-md p-6 sm:p-8 text-center"
            style={{ background: 'linear-gradient(180deg, #FFFFFF, #F0FDF4)', color: '#0B0F0C', boxShadow: '0 30px 70px rgba(0,0,0,0.5)' }}
            data-testid="mp-success-modal"
          >
            <div
              className="w-20 h-20 mx-auto rounded-2xl flex items-center justify-center mb-4"
              style={{ background: 'linear-gradient(180deg, #22C55E, #15803D)', boxShadow: '0 12px 30px rgba(34,197,94,0.55)' }}
            >
              <CheckCircle2 className="w-10 h-10 text-white" strokeWidth={2.5} />
            </div>
            <h2 className="display font-extrabold text-2xl sm:text-3xl mt-1 mb-2" style={{ color: '#0B0F0C' }}>
              Cashout Requested!
            </h2>
            <p className="text-sm" style={{ color: '#6B7280' }}>
              <span className="display font-bold" style={{ color: '#0B0F0C' }}>{redeemed.name}</span> · ${redeemed.cash.toLocaleString()} cash will be processed shortly.
            </p>
            <button
              onClick={() => setRedeemed(null)}
              className="mt-6 w-full h-12 rounded-2xl display font-extrabold text-white transition active:scale-[0.98] hover:brightness-110"
              style={{ background: 'linear-gradient(180deg, #22C55E, #15803D)', boxShadow: '0 8px 22px rgba(34,197,94,0.45)' }}
              data-testid="mp-success-ok"
            >
              Awesome!
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
